import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AllowanceForm as FormData, VacationCalculation } from '../types/allowance'
import { CityPolicy } from '../services/cityPolicyService'
import Calendar from './Calendar'

interface AllowanceFormProps {
  data: FormData
  vacationData: VacationCalculation
  onChange: (data: FormData) => void
  onSubmit: () => void
  loading?: boolean
  errorMessage?: string
  cityPolicy?: CityPolicy | null
}

const AllowanceForm: React.FC<AllowanceFormProps> = ({
  data,
  vacationData,
  onChange,
  onSubmit,
  loading,
  errorMessage,
  cityPolicy
}) => {
  // 本地状态：12个月工资填算弹窗 & 文件导入
  const [showAvgModal, setShowAvgModal] = useState(false)
  const [monthsInputs, setMonthsInputs] = useState<string[]>(Array(12).fill(''))
  const [bonusInput, setBonusInput] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // 防止弹窗打开时body滚动，并确保弹窗稳定性
  useEffect(() => {
    if (showAvgModal) {
      // 禁止body滚动
      document.body.style.overflow = 'hidden'
      // 确保弹窗渲染到正确位置
      document.body.style.position = 'relative'
    } else {
      // 恢复body滚动
      document.body.style.overflow = ''
      document.body.style.position = ''
    }

    // 清理函数
    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
    }
  }, [showAvgModal])
  
  // 需要显示月度工会费的城市
  const unionFeeCities = ['SH', 'SZ2', 'QD'] // 上海、苏州、青岛
  const shouldShowUnionFee = unionFeeCities.includes(vacationData.city)

  // 默认输入项配置（移除员工五险一金，将其单独处理）
  const defaultInputItems = [
    { key: 'employeeInsurance', label: '月度个人部分社保公积金合计', field: 'employeeInsurance' as keyof FormData, helperText: '员工个人承担的五险一金部分(包含补充公积金)' },
    { key: 'employerESPP', label: '月度ESPP', field: 'employerESPP' as keyof FormData },
    ...(shouldShowUnionFee ? [{ key: 'personalIncomeTax', label: '月度工会费', field: 'personalIncomeTax' as keyof FormData }] : []),
    { key: 'flexibleBenefit', label: '弹性福利自费', field: 'flexibleBenefit' as keyof FormData }
  ]

  // 移除了 visibleDefaultItems 和 showSpotOn 状态，因为默认项目现在不可删除

  // ESPP 启用勾选：默认启用
  const [esppEnabled, setEsppEnabled] = useState<boolean>(true)

  // 取用于计算ESPP默认值的基本工资
  const getBaseForEspp = (): number => {
    const toNum = (v?: string) => {
      if (!v) return 0
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }
    if (!data.crossApril) {
      return toNum(data.paidSalary as string)
    }
    const adj = toNum(data.adjustedMonthlyBaseSalary as string)
    if (adj > 0) return adj
    return toNum(data.monthlyBaseSalary as string)
  }

  // 当用户输入基本工资时：若勾选启用且当前ESPP为空或为0，则给一个10%的默认值
  const setEsppDefaultIfEmpty = (baseOverride?: number) => {
    if (!esppEnabled) return
    const cur = (data.employerESPP as string | undefined) ?? ''
    // 视空字符串或数值<=0为"空"
    const curNum = Number(cur)
    const isEmptyOrZero = cur === '' || (!isNaN(curNum) && isFinite(curNum) && curNum <= 0)
    if (!isEmptyOrZero) return
    const base = (typeof baseOverride === 'number' && isFinite(baseOverride)) ? baseOverride : getBaseForEspp()
    if (base > 0) {
      const val = (base * 0.10).toFixed(2)
      handleNumberChange('employerESPP', val)
    }
  }

  const handleChange = (field: keyof FormData, value: string | number | boolean | undefined) => {
    onChange({ ...data, [field]: value })
  }

  // 填算：打开/关闭弹窗
  const openAvgModal = () => {
    const currentAvg = data.avgSalary12Months || ''
    
    // 从 localStorage 读取填算数据
    const savedDataStr = localStorage.getItem('avgSalary_fillData')
    
    if (savedDataStr) {
      try {
        const savedData = JSON.parse(savedDataStr)
        
        // 检查当前的 avgSalary12Months 是否与上次计算的值一致
        if (currentAvg === savedData.calculatedAvg) {
          // 值未被修改，恢复填算数据
          console.log('📊 恢复填算数据:', savedData)
          setMonthsInputs(savedData.monthsInputs || Array(12).fill(''))
          setBonusInput(savedData.bonusInput || '')
        } else {
          // 值已被修改，清空填算数据
          console.log('🔄 avgSalary12Months已修改，清空填算数据')
          setMonthsInputs(Array(12).fill(''))
          setBonusInput('')
          // 清除 localStorage 中的数据
          localStorage.removeItem('avgSalary_fillData')
        }
      } catch (error) {
        console.error('解析填算数据失败:', error)
        setMonthsInputs(Array(12).fill(''))
        setBonusInput('')
      }
    } else {
      // 没有保存的数据，清空
      setMonthsInputs(Array(12).fill(''))
      setBonusInput('')
    }
    
    setShowAvgModal(true)
  }
  const closeAvgModal = () => setShowAvgModal(false)

  // 填算：输入单月工资（仅允许数字和一个小数点）
  const handleMonthInputChange = (index: number, value: string) => {
    if (isValidNumberInput(value) || value === '') {
      setMonthsInputs(prev => {
        const next = [...prev]
        next[index] = value
        return next
      })
    }
  }

  // 填算：输入框失焦时执行智能填充
  const handleMonthInputBlur = (index: number) => {
    setMonthsInputs(prev => {
      const currentValue = prev[index]
      
      // 1. 检查当前值是否有效
      if (currentValue === '' || parseFloat(currentValue) === 0 || isNaN(parseFloat(currentValue))) {
        return prev // 当前值无效，不执行填充
      }
      
      // 2. 检查其他月份是否都为空或0
      const hasNonZeroValue = prev.some((v, i) => {
        if (i === index) return false // 排除当前输入框
        const num = parseFloat(v)
        return v !== '' && !isNaN(num) && num !== 0
      })
      
      // 3. 如果所有其他月份都为空或0，则自动填充
      if (!hasNonZeroValue) {
        const next = [...prev]
        for (let i = 0; i < next.length; i++) {
          if (i !== index) {
            next[i] = currentValue
          }
        }
        return next
      }
      
      return prev
    })
  }

  // 填算：输入奖金
  const handleBonusInputChange = (value: string) => {
    if (isValidNumberInput(value) || value === '') {
      setBonusInput(value)
    }
  }

  // 填算：确认计算均值并写回 avgSalary12Months
  const confirmAvgFromMonths = () => {
    const nums = monthsInputs.map(v => {
      if (v === '' || v === undefined) return 0
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    })
    
    // 添加奖金到计算中
    const bonusNum = bonusInput === '' ? 0 : Number(bonusInput) || 0
    
    const sum = nums.reduce((acc, n) => acc + n, 0) + bonusNum
    const avg = nums.length > 0 ? sum / nums.length : 0
    const avgStr = avg.toFixed(2)
    
    // 保存填算数据到 localStorage
    const fillData = {
      monthsInputs,
      bonusInput,
      calculatedAvg: avgStr,
      timestamp: Date.now()
    }
    localStorage.setItem('avgSalary_fillData', JSON.stringify(fillData))
    
    // 使用 handleNumberChange 以沿用校验与字符串存储逻辑
    handleNumberChange('avgSalary12Months', avgStr)
    setShowAvgModal(false)
  }

  // 导出 Excel 模板
  const exportExcelTemplate = async () => {
    try {
      const XLSX = await import('xlsx')
      
      // 创建工作表数据 - 简单的两列格式：月份和工资
      const wsData = [
        ['月份', '工资'],
        ['第1个月', ''],
        ['第2个月', ''],
        ['第3个月', ''],
        ['第4个月', ''],
        ['第5个月', ''],
        ['第6个月', ''],
        ['第7个月', ''],
        ['第8个月', ''],
        ['第9个月', ''],
        ['第10个月', ''],
        ['第11个月', ''],
        ['第12个月', ''],
        ['奖金', '']
      ]
      
      // 创建工作表
      const ws = XLSX.utils.aoa_to_sheet(wsData)
      
      // 设置列宽
      ws['!cols'] = [
        { wch: 15 }, // 月份列
        { wch: 20 }  // 工资列
      ]
      
      // 创建工作簿
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '12个月工资')
      
      // 导出文件
      XLSX.writeFile(wb, '12个月工资模板.xlsx')
    } catch (err) {
      alert('导出失败，请确保已安装 xlsx 依赖')
      console.error('Export error:', err)
    }
  }

  // 导入 Excel/CSV：弹出文件选择
  const triggerImportExcel = () => {
    fileInputRef.current?.click()
  }

  // 解析CSV简单实现：提取前12个数字
  const parseCsvToAverage = async (text: string) => {
    const tokens = text.split(/[\n,;\t\s]+/).map(t => t.trim()).filter(Boolean)
    const nums: number[] = []
    for (let i = 0; i < tokens.length && nums.length < 12; i++) {
      const t = tokens[i]
      if (/^\d*(\.\d+)?$/.test(t) && t !== '.') {
        const n = Number(t)
        if (Number.isFinite(n)) nums.push(n)
      }
    }
    const filled = [...nums]
    while (filled.length < 12) filled.push(0)
    const sum = filled.slice(0,12).reduce((a,b)=>a+b,0)
    const avg = sum / 12
    handleNumberChange('avgSalary12Months', avg.toFixed(2))
  }

  // 解析 xlsx（可选）：动态加载 xlsx，若失败给出提示
  const parseXlsxToAverage = async (file: File) => {
    try {
      const XLSX = await import('xlsx')
      const data = await file.arrayBuffer()
      const wb = XLSX.read(data, { type: 'array' })
      const sheet = wb.Sheets[wb.SheetNames[0]]
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as Array<Array<unknown>>
      
      // 检查是否是标准模板格式（第一行是表头：月份、工资）
      const isTemplateFormat = json.length > 0 && 
        Array.isArray(json[0]) && 
        json[0].length >= 2 &&
        (String(json[0][0]).includes('月份') || String(json[0][0]).includes('Month'))
      
      const monthValues: string[] = Array(12).fill('')
      let bonusValue = ''
      
      if (isTemplateFormat) {
        // 使用模板格式解析：从第二行开始读取工资列（索引1）
        let monthIndex = 0
        for (let i = 1; i < json.length && i <= 13; i++) {
          const row = json[i] as Array<unknown>
          if (row && row.length >= 2) {
            const label = String(row[0] || '').trim()
            const value = String(row[1] || '').trim()
            
            if (label.includes('奖金') || label.toLowerCase().includes('bonus')) {
              bonusValue = value
            } else if (monthIndex < 12) {
              // 保留空值，确保位置对应正确
              monthValues[monthIndex] = value
              monthIndex++
            }
          }
        }
        
        // 更新弹窗中的输入框 - 直接使用解析的数组
        setMonthsInputs(monthValues)
        
        // 更新奖金输入框
        setBonusInput(bonusValue)
        
        alert('Excel 导入成功！请检查数据并点击确认。')
      } else {
        // 兼容旧格式：提取所有数字
        const flat = json.flat()
        const nums: number[] = []
        for (let i = 0; i < flat.length && nums.length < 12; i++) {
          const v = flat[i]
          const n = typeof v === 'number' ? v : (typeof v === 'string' && /^\d*(\.\d+)?$/.test(v) ? Number(v) : NaN)
          if (Number.isFinite(n)) nums.push(n as number)
        }
        const filled = [...nums]
        while (filled.length < 12) filled.push(0)
        const sum = filled.slice(0,12).reduce((a,b)=>a+b,0)
        const avg = sum / 12
        handleNumberChange('avgSalary12Months', avg.toFixed(2))
      }
    } catch (err) {
      alert('未检测到 xlsx 解析能力，请安装 xlsx 依赖或导入 CSV 文件。')
    }
  }

  const handleExcelFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.currentTarget.value = '' // 允许选择相同文件再次触发
    if (!file) return
    const name = file.name.toLowerCase()
    if (name.endsWith('.csv')) {
      const text = await file.text()
      await parseCsvToAverage(text)
      return
    }
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      await parseXlsxToAverage(file)
      return
    }
    alert('仅支持导入 .xlsx / .xls / .csv 文件')
  }

  // 添加自定义输入项
  const addCustomInput = () => {
    const newCustomInputs = [...(data.customInputs || []), {
      id: `custom_${Date.now()}`,
      label: '',
      value: '',
      isEditing: true // 标记为编辑状态
    }]
    onChange({ ...data, customInputs: newCustomInputs })
  }

  // 添加自定义减项
  const addCustomDeduction = () => {
    const newCustomDeductions = [...(data.customDeductions || []), {
      id: `deduction_${Date.now()}`,
      label: '',
      value: '',
      isEditing: true // 标记为编辑状态
    }]
    onChange({ ...data, customDeductions: newCustomDeductions })
  }

  // 检查是否有正在编辑的自定义输入项
  const hasEditingCustomInput = () => {
    return (data.customInputs || []).some(item => item.isEditing)
  }

  // 获取正在编辑的自定义输入项
  const getEditingCustomInput = () => {
    return (data.customInputs || []).find(item => item.isEditing)
  }

  // 确认自定义输入项
  const confirmCustomInput = () => {
    const editingItem = getEditingCustomInput()
    if (editingItem && editingItem.label.trim()) {
      finishCustomInputEdit(editingItem.id)
    }
  }

  // 检查是否有正在编辑的自定义减项
  const hasEditingCustomDeduction = () => {
    return (data.customDeductions || []).some(item => item.isEditing)
  }

  // 获取正在编辑的自定义减项
  const getEditingCustomDeduction = () => {
    return (data.customDeductions || []).find(item => item.isEditing)
  }

  // 确认自定义减项
  const confirmCustomDeduction = () => {
    const editingItem = getEditingCustomDeduction()
    if (editingItem && editingItem.label.trim()) {
      finishCustomDeductionEdit(editingItem.id)
    }
  }

  // 删除自定义输入项
  const removeCustomInput = (id: string) => {
    const newCustomInputs = (data.customInputs || []).filter(item => item.id !== id)
    onChange({ ...data, customInputs: newCustomInputs })
  }

  // 更新自定义输入项
  const updateCustomInput = (id: string, field: 'label' | 'value', newValue: string) => {
    const newCustomInputs = (data.customInputs || []).map(item => 
      item.id === id ? { ...item, [field]: newValue } : item
    )
    onChange({ ...data, customInputs: newCustomInputs })
  }

  // 完成自定义输入项编辑
  const finishCustomInputEdit = (id: string) => {
    const newCustomInputs = (data.customInputs || []).map(item => 
      item.id === id ? { ...item, isEditing: false } : item
    )
    onChange({ ...data, customInputs: newCustomInputs })
  }

  // 删除自定义减项
  const removeCustomDeduction = (id: string) => {
    const newCustomDeductions = (data.customDeductions || []).filter(item => item.id !== id)
    onChange({ ...data, customDeductions: newCustomDeductions })
  }

  // 更新自定义减项
  const updateCustomDeduction = (id: string, field: 'label' | 'value', newValue: string) => {
    const newCustomDeductions = (data.customDeductions || []).map(item => 
      item.id === id ? { ...item, [field]: newValue } : item
    )
    onChange({ ...data, customDeductions: newCustomDeductions })
  }

  // 完成自定义减项编辑
  const finishCustomDeductionEdit = (id: string) => {
    const newCustomDeductions = (data.customDeductions || []).map(item => 
      item.id === id ? { ...item, isEditing: false } : item
    )
    onChange({ ...data, customDeductions: newCustomDeductions })
  }

  // removeDefaultItem 函数已移除，因为默认项目不可删除

  // 验证数字输入格式
  const isValidNumberInput = (value: string): boolean => {
    if (value === '') return true
    
    // 允许输入过程中的中间状态：
    // - 整数: "0", "123"
    // - 整数+小数点: "12."
    // - 完整小数: "12.34"
    // - 纯小数: ".5"
    // 不允许: 多个小数点、字母、负号、以小数点结尾后再有小数点等
    
    // 检查是否包含多个小数点
    const dotCount = (value.match(/\./g) || []).length
    if (dotCount > 1) return false
    
    // 使用正则验证基本格式：数字、最多一个小数点、数字
    return /^\d*\.?\d*$/.test(value) && value !== '.'
  }

  // 处理数字输入框的输入事件（实时过滤）
  const handleNumberInput = (e: React.FormEvent<HTMLInputElement>, field: keyof FormData) => {
    const input = e.currentTarget
    const value = input.value
    
    // 实时过滤：只保留数字和最多一个小数点
    let filteredValue = ''
    let hasDot = false
    
    for (let i = 0; i < value.length; i++) {
      const char = value[i]
      if (/\d/.test(char)) {
        filteredValue += char
      } else if (char === '.' && !hasDot) {
        filteredValue += char
        hasDot = true
      }
      // 其他字符（包括字母、多余的小数点等）都被忽略
    }
    
    // 如果过滤后的值与原值不同，更新输入框
    if (filteredValue !== value) {
      input.value = filteredValue
    }
    
    // 更新状态
    handleChange(field, filteredValue === '' ? undefined : filteredValue)
  }

  // 处理数字输入框的变化事件（兼容性）
  const handleNumberChange = (field: keyof FormData, value: string) => {
    // 直接存储字符串，避免数字转换导致的精度丢失
    if (value === '' || isValidNumberInput(value)) {
      handleChange(field, value === '' ? undefined : value)
    }
    // 无效输入直接忽略，保持原值
  }

  // 获取数字输入框的显示值
  const getNumberDisplayValue = (value: string | undefined): string => {
    return value || ''
  }

  // 本地数字输入清洗，与 handleNumberInput 的过滤规则一致
  const sanitizeNumberInput = (value: string): string => {
    let filteredValue = ''
    let hasDot = false
    for (let i = 0; i < value.length; i++) {
      const char = value[i]
      if (/\d/.test(char)) {
        filteredValue += char
      } else if (char === '.' && !hasDot) {
        filteredValue += char
        hasDot = true
      }
    }
    return filteredValue
  }

  // 计算是否需要显示跨四月选项
  const shouldShowCrossApril = () => {
    if (!vacationData?.startDate || !vacationData?.calculatedDays) {
      return false
    }
    
    const startDate = new Date(vacationData.startDate)
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + vacationData.calculatedDays)
    
    // 检查产假期间是否包含任何年份的4月份
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    
    // 遍历产假期间涉及的所有年份，检查是否包含4月份
    for (let year = startYear; year <= endYear; year++) {
      const aprilStart = new Date(year, 3, 1) // 4月1日
      const aprilEnd = new Date(year, 3, 30)   // 4月30日
      
      // 检查产假期间是否与该年的4月份有重叠
      if (startDate <= aprilEnd && endDate >= aprilStart) {
        return true
      }
    }
    
    return false
  }

  // 计算是否需要显示跨七月选项
  const shouldShowCrossJuly = () => {
    if (!vacationData?.startDate || !vacationData?.calculatedDays) {
      return false
    }
    
    const startDate = new Date(vacationData.startDate)
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + vacationData.calculatedDays)
    
    // 检查产假期间是否包含任何年份的7月份
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    
    // 遍历产假期间涉及的所有年份，检查是否包含7月份
    for (let year = startYear; year <= endYear; year++) {
      const julyStart = new Date(year, 6, 1) // 7月1日
      const julyEnd = new Date(year, 6, 31)   // 7月31日
      
      // 检查产假期间是否与该年的7月份有重叠
      if (startDate <= julyEnd && endDate >= julyStart) {
        return true
      }
    }
    
    return false
  }


  // 检查是否应该显示单位申报月平均工资
  const shouldShowCompanyPay = () => {
    if (!cityPolicy) return false
    return cityPolicy.declaredSalary === true
  }

  return (
    <>
      {/* 填算12个月工资的弹窗 - 使用Portal渲染到body */}
      {showAvgModal && createPortal(
        <div 
          className="salary-modal-overlay" 
          onClick={closeAvgModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--modal-overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            margin: 0,
            boxSizing: 'border-box'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 30px var(--shadow-heavy)',
              position: 'relative',
              maxHeight: '90vh',
              overflow: 'hidden',
              margin: 0,
              maxWidth: '800px',
              width: '90%',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '1.5',
              color: 'var(--color-text-primary)'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px',
              borderBottom: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-background-light)'
            }}>
              <h3 style={{
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--color-text-primary)',
                fontSize: '1.2rem'
              }}>
                <i className="fas fa-calculator" />
                填写近12个月工资
              </h3>
              <button 
                onClick={closeAvgModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: '5px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-error)'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'var(--color-text-secondary)'
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              maxHeight: 'calc(90vh - 120px)'
            }}>
              {/* 导入/导出Excel按钮 */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <button
                  type="button"
                  onClick={exportExcelTemplate}
                  title="导出 Excel 模板"
                  style={{
                    background: 'var(--success-color)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--success-hover-color)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--success-color)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <i className="fas fa-file-export"></i> 导出模板
                </button>
                <button
                  type="button"
                  onClick={triggerImportExcel}
                  title="导入 Excel 文件"
                  style={{
                    background: 'var(--color-blue)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-blue-dark)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-blue)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <i className="fas fa-file-import"></i> 导入Excel
                </button>
              </div>
              
              {/* 12个月工资输入 - 每行4个 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {monthsInputs.map((v, idx) => (
                  <div key={idx} style={{ marginBottom: '12px' }}>
                    <label style={{
                      fontSize: '0.9rem',
                      marginBottom: '4px',
                      display: 'block',
                      color: 'var(--color-text-primary)',
                      fontWeight: '500'
                    }}>第 {idx+1} 个月</label>
                    <input
                      type="text"
                      value={v}
                      onChange={(e) => handleMonthInputChange(idx, e.target.value)}
                      placeholder="0.00"
                      style={{
                        padding: '8px 12px',
                        fontSize: '0.9rem',
                        border: '1px solid var(--color-border)',
                        borderRadius: '6px',
                        width: '100%',
                        boxSizing: 'border-box',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-blue)'
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-border)'
                        handleMonthInputBlur(idx) // 失焦时执行智能填充
                      }}
                    />
                  </div>
                ))}
              </div>
              
              {/* 奖金输入 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    fontSize: '0.9rem',
                    marginBottom: '4px',
                    display: 'block',
                    color: 'var(--color-text-primary)',
                    fontWeight: '500'
                  }}>奖金</label>
                  <input
                    type="text"
                    value={bonusInput}
                    onChange={(e) => handleBonusInputChange(e.target.value)}
                    placeholder="0.00"
                    style={{
                      padding: '8px 12px',
                      fontSize: '0.9rem',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      width: '100%',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-blue)'
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-border)'
                    }}
                  />
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              padding: '15px 20px',
              borderTop: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-background-light)'
            }}>
              <button 
                onClick={confirmAvgFromMonths}
                style={{
                  backgroundColor: 'var(--color-blue)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-blue-dark)'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-blue)'
                }}
              >
                <i className="fas fa-check" /> 确认
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 津贴计算部分 */}
      <div className="allowance-section">
        <h3 className="section-title">
          <i className="fas fa-money-bill-wave"></i>
          津贴计算
        </h3>
        
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="governmentAmount" className="form-label">
              <i className="fas fa-money-bill-wave"></i>
              政府发放金额总数
            </label>
            <input
              type="text"
              id="governmentAmount"
              name="governmentAmount"
              autoComplete="on"
              value={getNumberDisplayValue(data.governmentAmount)}
              onInput={(e) => handleNumberInput(e, 'governmentAmount')}
              placeholder="请输入政府发放金额"
              className="form-input no-spinner"
              step="0.01"
              min="0"
            />
          </div>
          {/* 工资信息部分 - 所有城市都显示 */}
          <div className="infomation-salary">
          </div>
          
          {shouldShowCompanyPay() && (
            <div className="form-group">
              <label htmlFor="declaredSalary" className="form-label">
                <i className="fas fa-file-alt"></i>
                单位申报的上年度月平均工资
              </label>
              <input
                type="text"
                id="declaredSalary"
                name="declaredSalary"
                autoComplete="on"
                value={getNumberDisplayValue(data.declaredSalary)}
                onInput={(e) => handleNumberInput(e, 'declaredSalary')}
                placeholder="请输入单位申报工资"
                className="form-input no-spinner"
                step="0.01"
                min="0"
              />
            </div>
          )}

          <div className="form-group">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
              <label htmlFor="avgSalary12Months" className="form-label" style={{ marginBottom: 0 }}>
                <i className="fas fa-chart-line"></i>
                员工产前12个月的月平均工资
              </label>
              <div style={{ display:'flex', gap: '14px' }}>
                <button
                  type="button"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    minWidth: 'auto',
                    color: 'var(--color-blue)',
                    cursor: 'pointer'
                  }}
                  onClick={openAvgModal}
                  title="填算"
                >
                  <i className="fas fa-calculator"></i> 填算
                </button>
              </div>
            </div>
            <input
              type="text"
              id="avgSalary12Months"
              name="avgSalary12Months"
              autoComplete="on"
              value={getNumberDisplayValue(data.avgSalary12Months)}
              onInput={(e) => handleNumberInput(e, 'avgSalary12Months')}
              placeholder="请输入产前12个月月均工资"
              className="form-input no-spinner"
              step="0.01"
              min="0"
            />
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelFileSelected} style={{ display:'none' }} />
          </div>

          {shouldShowCrossApril() && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="crossApril"
                  checked={data.crossApril}
                  onChange={(e) => handleChange('crossApril', e.target.checked)}
                />
                <span className="checkmark"></span>
                产假期间如有调薪请勾选
              </label>
              <small className="form-help">产假期间跨越4月25号，可选择分别填写4月前后的工资</small>
            </div>
          )}

          <>
            {!data.crossApril ? (
              <div className="form-group">
                <label htmlFor="paidSalary" className="form-label">
                  <i className="fas fa-wallet"></i>
                  员工的基本工资
                </label>
                <input
                  type="text"
                  id="paidSalary"
                  name="paidSalary"
                  autoComplete="on"
                  value={getNumberDisplayValue(data.paidSalary)}
                  onInput={(e) => {
                    const raw = e.currentTarget.value || ''
                    const filtered = sanitizeNumberInput(raw)
                    if (filtered !== raw) e.currentTarget.value = filtered
                    
                    // 组装一次性更新，避免多次状态更新导致的显示问题
                    const nextData: any = { ...data, paidSalary: filtered === '' ? undefined : filtered }
                    
                    // 检查是否需要设置ESPP默认值
                    if (esppEnabled) {
                      const curEspp = (data.employerESPP as string | undefined) ?? ''
                      const curEsppNum = Number(curEspp)
                      const isEmptyOrZero = curEspp === '' || (!isNaN(curEsppNum) && isFinite(curEsppNum) && curEsppNum <= 0)
                      const baseNum = Number(filtered)
                      if (isEmptyOrZero && Number.isFinite(baseNum) && baseNum > 0) {
                        nextData.employerESPP = (baseNum * 0.10).toFixed(2)
                      }
                    }
                    
                    onChange(nextData)
                  }}
                  placeholder="请输入已发放产假工资"
                  className="form-input no-spinner"
                  step="0.01"
                  min="0"
                />
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="monthlyBaseSalary" className="form-label">
                    <i className="fas fa-wallet"></i>
                    调整前员工的基本工资 <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="monthlyBaseSalary"
                    name="monthlyBaseSalary"
                    autoComplete="on"
                    value={getNumberDisplayValue(data.monthlyBaseSalary)}
                    onInput={(e) => {
                      const raw = e.currentTarget.value || ''
                      const filtered = sanitizeNumberInput(raw)
                      if (filtered !== raw) e.currentTarget.value = filtered
                      
                      const nextData: any = { ...data, monthlyBaseSalary: filtered === '' ? undefined : filtered }
                      
                      if (esppEnabled) {
                        const curEspp = (data.employerESPP as string | undefined) ?? ''
                        const curEsppNum = Number(curEspp)
                        const isEmptyOrZero = curEspp === '' || (!isNaN(curEsppNum) && isFinite(curEsppNum) && curEsppNum <= 0)
                        const baseNum = Number(filtered)
                        if (isEmptyOrZero && Number.isFinite(baseNum) && baseNum > 0) {
                          nextData.employerESPP = (baseNum * 0.10).toFixed(2)
                        }
                      }
                      
                      onChange(nextData)
                    }}
                    placeholder="请输入调整前基本工资"
                    className="form-input no-spinner"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="adjustedMonthlyBaseSalary" className="form-label">
                    <i className="fas fa-wallet"></i>
                    调整后员工的基本工资
                  </label>
                  <input
                    type="text"
                    id="adjustedMonthlyBaseSalary"
                    name="adjustedMonthlyBaseSalary"
                    autoComplete="on"
                    value={getNumberDisplayValue(data.adjustedMonthlyBaseSalary)}
                    onInput={(e) => {
                      const raw = e.currentTarget.value || ''
                      const filtered = sanitizeNumberInput(raw)
                      if (filtered !== raw) e.currentTarget.value = filtered
                      
                      const nextData: any = { ...data, adjustedMonthlyBaseSalary: filtered === '' ? undefined : filtered }
                      
                      // 当 crossApril 为 true 时，ESPP 不跟随调整后工资变化
                      if (esppEnabled && !data.crossApril) {
                        const curEspp = (data.employerESPP as string | undefined) ?? ''
                        const curEsppNum = Number(curEspp)
                        const isEmptyOrZero = curEspp === '' || (!isNaN(curEsppNum) && isFinite(curEsppNum) && curEsppNum <= 0)
                        const baseNum = Number(filtered)
                        if (isEmptyOrZero && Number.isFinite(baseNum) && baseNum > 0) {
                          nextData.employerESPP = (baseNum * 0.10).toFixed(2)
                        }
                      }
                      
                      onChange(nextData)
                    }}
                    placeholder="请输入调整后基本工资"
                    className="form-input no-spinner"
                    step="0.01"
                    min="0"
                  />
                </div>
              </>
            )}
          </>

          {/* 提交核定表时间 */}
          <div className="form-group">
            <Calendar
              selectedDate={data.receivedTime ? new Date(data.receivedTime) : null}
              onDateSelect={(date) => {
                if (date) {
                  // 使用本地时间格式化，避免时区转换问题
                  const year = date.getFullYear()
                  const month = String(date.getMonth() + 1).padStart(2, '0')
                  const day = String(date.getDate()).padStart(2, '0')
                  const dateString = `${year}-${month}-${day}`
                  handleChange('receivedTime', dateString)
                } else {
                  handleChange('receivedTime', undefined)
                }
              }}
              label="提交核定表时间"
              helpText="请选择提交核定表时间"
              defaultCollapsed={false}
            />
          </div>
        </div>

        {/* 4. 返还金额信息 - 仅特定城市显示 */}
        {['TJ', 'SX', 'XM', 'SH', 'QD'].includes(data.city) && (
          <div className="form-section">
            <h3 className="section-title">
              <i className="fas fa-undo"></i>
              返还金额信息
            </h3>
            <p className="section-note">适用城市：天津、绍兴、厦门、上海、青岛</p>
            
            {/* 产假提交时间 */}
            <div className="form-group">
              <Calendar
                selectedDate={data.maternitySubmissionDate ? new Date(data.maternitySubmissionDate) : (vacationData.startDate ? new Date(vacationData.startDate) : null)}
                onDateSelect={(date) => {
                  if (date) {
                    // 使用本地时间格式化，避免时区转换问题
                    const year = date.getFullYear()
                    const month = String(date.getMonth() + 1).padStart(2, '0')
                    const day = String(date.getDate()).padStart(2, '0')
                    const dateString = `${year}-${month}-${day}`
                    console.log('📅 AllowanceForm onDateSelect called with:', date.toDateString(), 'formatted as:', dateString)
                    handleChange('maternitySubmissionDate', dateString)
                  } else {
                    handleChange('maternitySubmissionDate', undefined)
                  }
                }}
                label="产假提交时间"
                helpText="请选择产假提交时间"
                defaultCollapsed={true}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-coins"></i>
                公司垫付（未填写项默认为0）
              </label>
            </div>
            <div className="form-group" style={{ display: data.advanceInputMethod === 'detailed' ? 'block' : 'none' }}>
              <div className="detailed-inputs">
              <h4 style={{ color: 'var(--error-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-plus-circle"></i>
                 增项
              </h4>
                {/* 默认输入项 */}
                {defaultInputItems.map(item => (
                  <div key={item.key} className="input-row">
                    <label htmlFor={item.key} className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {item.label}
                      {item.key === 'employerESPP' && (
                        <label
                          className="checkbox-label"
                          style={{
                            margin: 0,
                            marginLeft: '16px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={esppEnabled}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setEsppEnabled(checked)
                              if (checked) {
                                // 勾选 -> 若当前为空/0，按当前基薪给默认10%
                                setEsppDefaultIfEmpty()
                              } else {
                                // 未勾选 -> 强制为0
                                handleNumberChange('employerESPP', '0')
                              }
                            }}
                            style={{ verticalAlign: 'middle', width: '16px', height: '16px', display: 'inline-block' }}
                          />
                          <span className="checkmark checkmarkEspp" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px' }}></span>
                          是否参与ESPP计划
                        </label>
                      )}
                    </label>
                    {item.key === 'employeeInsurance' && shouldShowCrossJuly() ? (
                      // 跨七月时显示两个输入框
                      <div className="dual-input-container">
                        <div className="input-with-button half-width">
                          <input
                            type="text"
                            id={`${item.key}Before`}
                            name={`${item.key}Before`}
                            autoComplete="on"
                            value={getNumberDisplayValue(data.socialInsuranceBase as string)}
                            onChange={(e) => {
                              if (isValidNumberInput(e.target.value)) {
                                handleNumberChange('socialInsuranceBase', e.target.value)
                              }
                            }}
                            placeholder="调整前的个人社保公积金合计"
                            className="form-input no-spinner"
                          />
                        </div>
                        <div className="input-with-button half-width">
                          <input
                            type="text"
                            id={`${item.key}After`}
                            name={`${item.key}After`}
                            autoComplete="on"
                            value={getNumberDisplayValue(data.adjustedSocialInsuranceBase as string)}
                            onChange={(e) => {
                              if (isValidNumberInput(e.target.value)) {
                                handleNumberChange('adjustedSocialInsuranceBase', e.target.value)
                              }
                            }}
                            placeholder="调整后的个人社保公积金合计"
                            className="form-input no-spinner"
                          />
                        </div>
                        <button
                          type="button"
                          className="remove-btn disabled"
                          disabled
                          title="默认项目不可删除"
                        >
                          <i className="fas fa-lock"></i>
                        </button>
                      </div>
                    ) : (
                      // 正常情况显示单个输入框
                      <div className="input-with-button">
                        <input
                          type="text"
                          id={item.key}
                          name={item.key}
                          autoComplete="on"
                          value={getNumberDisplayValue(data[item.field] as string)}
                          onInput={(e) => {
                            const raw = e.currentTarget.value || ''
                            const filtered = sanitizeNumberInput(raw)
                            if (filtered !== raw) e.currentTarget.value = filtered
                            
                            handleNumberChange(item.field, filtered === '' ? '' : filtered)
                            
                            // 若用户手动输入ESPP且数值>0，自动勾选
                            if (item.key === 'employerESPP') {
                              const n = Number(filtered)
                              if (Number.isFinite(n) && n > 0 && !esppEnabled) {
                                setEsppEnabled(true)
                              }
                            }
                          }}
                          placeholder={`请输入${item.label}`}
                          className="form-input no-spinner"
                        />
                        <button
                          type="button"
                          className="remove-btn disabled"
                          disabled
                          title="默认项目不可删除"
                        >
                          <i className="fas fa-lock"></i>
                        </button>
                      </div>
                    )}
                    {
                      item.helperText && (
                        <small className="form-help">{item.helperText}</small>
                      )
                    }
                  </div>
                ))}

                {/* 自定义输入项 */}
                {(data.customInputs || []).map(customItem => (
                  <div key={customItem.id} className="input-row">
                    {customItem.isEditing ? (
                      <div className="input-with-button">
                        <input
                          type="text"
                          value={customItem.label}
                          onChange={(e) => updateCustomInput(customItem.id, 'label', e.target.value)}
                          placeholder="请输入标签名称"
                          className="form-input label-input"
                          onBlur={() => {
                            if (customItem.label.trim()) {
                              finishCustomInputEdit(customItem.id)
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && customItem.label.trim()) {
                              finishCustomInputEdit(customItem.id)
                            }
                          }}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => removeCustomInput(customItem.id)}
                          className="remove-btn"
                          title="删除此项"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <label htmlFor={customItem.id} className="form-label">{customItem.label}</label>
                        <div className="input-with-button">
                          <input
                            type="text"
                            id={customItem.id}
                            name={customItem.id}
                            autoComplete="on"
                            value={customItem.value}
                            onChange={(e) => {
                              if (isValidNumberInput(e.target.value)) {
                                updateCustomInput(customItem.id, 'value', e.target.value)
                              }
                            }}
                            placeholder="请输入金额"
                            className="form-input no-spinner"
                          />
                          <button
                            type="button"
                            onClick={() => removeCustomInput(customItem.id)}
                            className="remove-btn"
                            title="删除此项"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* 添加自定义输入项按钮 */}
                <div className="add-custom-row">
                  <button
                    type="button"
                    onClick={hasEditingCustomInput() ? confirmCustomInput : addCustomInput}
                    className={`add-custom-btn ${hasEditingCustomInput() ? (getEditingCustomInput()?.label.trim() ? 'enabled' : 'disabled') : ''}`}
                    disabled={hasEditingCustomInput() && !getEditingCustomInput()?.label.trim()}
                  >
                    <i className={`fas ${hasEditingCustomInput() ? 'fa-check' : 'fa-plus'}`}></i>
                    {hasEditingCustomInput() ? '确定' : '添加'}
                  </button>
                </div>

                {/* 减项信息分隔线 */}
                <div style={{ margin: '20px 0', borderTop: '1px dashed var(--border-light)', paddingTop: '20px' }}>
                  <h4 style={{ color: 'var(--error-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-minus-circle"></i>
                    减项
                  </h4>
                  
                  {/* 默认减项 - spot on (不可删除) */}
                  <div className="input-row">
                    <label className="form-label">spot on</label>
                    <div className="input-with-button">
                      <input
                        type="text"
                        name="spotOn"
                        autoComplete="on"
                        value={getNumberDisplayValue(data.spotOn || '')}
                        onChange={(e) => {
                          if (isValidNumberInput(e.target.value)) {
                            handleChange('spotOn', e.target.value)
                          }
                        }}
                        placeholder="请输入spot on金额"
                        className="form-input no-spinner"
                      />
                      <button
                        type="button"
                        className="remove-btn disabled"
                        disabled
                        title="默认项目不可删除"
                      >
                        <i className="fas fa-lock"></i>
                      </button>
                    </div>
                  </div>

                  {/* 自定义减项 */}
                  {(data.customDeductions || []).map(deductionItem => (
                    <div key={deductionItem.id} className="input-row">
                      {deductionItem.isEditing ? (
                        <div className="input-with-button">
                          <input
                            type="text"
                            value={deductionItem.label}
                            onChange={(e) => updateCustomDeduction(deductionItem.id, 'label', e.target.value)}
                            placeholder="请输入标签名称"
                            className="form-input label-input"
                            onBlur={() => {
                              if (deductionItem.label.trim()) {
                                finishCustomDeductionEdit(deductionItem.id)
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && deductionItem.label.trim()) {
                                finishCustomDeductionEdit(deductionItem.id)
                              }
                            }}
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => removeCustomDeduction(deductionItem.id)}
                            className="remove-btn"
                            title="删除此项"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <label htmlFor={deductionItem.id} className="form-label">{deductionItem.label}</label>
                          <div className="input-with-button">
                            <input
                              type="text"
                              id={deductionItem.id}
                              name={deductionItem.id}
                              autoComplete="on"
                              value={deductionItem.value}
                              onChange={(e) => {
                                if (isValidNumberInput(e.target.value)) {
                                  updateCustomDeduction(deductionItem.id, 'value', e.target.value)
                                }
                              }}
                              placeholder="请输入扣除金额"
                              className="form-input no-spinner"
                            />
                            <button
                              type="button"
                              onClick={() => removeCustomDeduction(deductionItem.id)}
                              className="remove-btn"
                              title="删除此项"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {/* 添加自定义减项按钮 */}
                  <div className="add-custom-row">
                    <button
                      type="button"
                      onClick={hasEditingCustomDeduction() ? confirmCustomDeduction : addCustomDeduction}
                      className={`add-custom-btn ${hasEditingCustomDeduction() ? (getEditingCustomDeduction()?.label.trim() ? 'enabled' : 'disabled') : ''}`}
                      disabled={hasEditingCustomDeduction() && !getEditingCustomDeduction()?.label.trim()}
                    >
                      <i className={`fas ${hasEditingCustomDeduction() ? 'fa-check' : 'fa-plus'}`}></i>
                      {hasEditingCustomDeduction() ? '确定' : '添加'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>


      {errorMessage && (
        <div className="error-message" style={{ color: 'var(--error-color)', marginBottom: '10px', padding: '8px', backgroundColor: 'var(--error-bg-light)', border: '1px solid var(--error-bg)', borderRadius: '4px' }}>
          {errorMessage}
        </div>
      )}

      <button type="button" onClick={onSubmit} className="calculate-btn" disabled={loading}>
        <i className="fas fa-calculator"></i>
        {loading ? '计算中...' : '计算补差金额'}
      </button>
    </>
  )
}

export default AllowanceForm
